import React, { useEffect, useState } from "react";
import { Loader2, Upload } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { formatApiError } from "../../lib/errors";
import { SLOT_LABEL } from "../../lib/subby";
import { Field, Input, Modal, PrimaryButton, QuietButton } from "../UI";
import { PdfPicker, checkPdf } from "./SubbyQuoteDialog";

/** LT-37 — add the next revision to a quote's `original` or `client` slot. Earlier revisions stay. */
export function SubbyQuoteFileDialog({ open, onClose, tenderId, quote, slot, onSaved }) {
  const [file, setFile] = useState(null);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  useEffect(() => { if (open) { setFile(null); setNote(""); setError(null); } }, [open]);

  const nextRev = ((quote?.files?.[slot] || []).length || 0) + 1;

  const save = async () => {
    const problem = !file ? "Choose a PDF." : checkPdf(file, SLOT_LABEL[slot]);
    if (problem) { setError(problem); return; }
    setSaving(true); setError(null);
    const fd = new FormData();
    fd.append("file", file);
    if (note.trim()) fd.append("note", note.trim());
    try {
      const r = await api.post(`/tenders/${tenderId}/subby-quotes/${quote.id}/files/${slot}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${SLOT_LABEL[slot]} revision ${nextRev} added`);
      onSaved?.(r.data); onClose();
    } catch (e) { setError(formatApiError(e, "Upload failed.")); }
    finally { setSaving(false); }
  };

  if (!quote || !slot) return null;
  return (
    <Modal open={open} onClose={onClose} title={`${SLOT_LABEL[slot]} — add revision ${nextRev}`} testid="subby-file-dialog"
      footer={<>
        <QuietButton onClick={onClose} data-testid="subby-file-dialog-cancel">Cancel</QuietButton>
        <PrimaryButton onClick={save} disabled={saving} icon={saving ? Loader2 : Upload} data-testid="subby-file-dialog-save">Add revision</PrimaryButton>
      </>}>
      <div className="text-ink-2 mb-4" style={{ fontSize: 12.5 }}>
        {quote.subcontractor_name} · {nextRev === 1 ? "no file in this slot yet" : `revision ${nextRev - 1} stays on record`}. Files are never replaced.
      </div>
      <div className="grid gap-4">
        <PdfPicker label="PDF" required file={file} onChange={setFile} testid="subby-file-dialog-file" />
        <Field label="What changed" hint="Shown against this revision.">
          <Input value={note} onChange={setNote} placeholder="e.g. revised price after site visit" data-testid="subby-file-dialog-note" />
        </Field>
      </div>
      {error && <div className="mt-4 rounded-md border px-3 py-2" style={{ background: "#FBECEC", borderColor: "#E9C0C0", color: "#7A1F1F", fontSize: 12 }} data-testid="subby-file-dialog-error">{error}</div>}
    </Modal>
  );
}
